import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { ProceedModalComponent } from '@components/proceed-modal/proceed-modal.component';
import { fade } from '@core/animations/animations';
import { ICON } from '@core/util/icon';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot, ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { SnackbarService } from '@services/ui/snackbar.service';
import { UrlService } from '@services/url/url.service';
import { SelectedMatterDocumentsDispatch } from '@state/global/documents/selected-matter-documents.dispatch';
import {
	DOCUMENT_ACTIONS,
	DocumentResult,
	documentActionData,
} from '@state/global/documents/selected-matter-documents.model';
import { SelectedMatterDocumentsState } from '@state/global/documents/selected-matter-documents.state';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { RecentlyViewedDispatch } from '@state/recently-viewed/recently-viewed.dispatch';
import LogRocket from 'logrocket';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import {
	debounceTime,
	distinctUntilChanged,
	filter,
	map,
	take,
} from 'rxjs/operators';
import {
	DropdownResult,
	SelectionGroup,
} from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { BreadcrumbItem } from 'src/app/shared/atomic/molecules/breadcrumb/breadcrumb.model';
import {
	InputModalComponent,
	InputModalData,
} from 'src/app/shared/atomic/molecules/input-modal/input-modal.component';
import { NavigationService } from 'src/app/shared/services/nav/nav.service';
import { DocumentComponentSelector } from './document.selector';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';
import tinymce from 'tinymce';

@UntilDestroy()
@Component({
	selector: 'app-document',
	templateUrl: './document.component.html',
	animations: [fade()],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class DocumentComponent implements OnInit {
	private static nonExistantTimeoutMs = 10000;
	private static displayNonExistantMessageForMs = 5000;

	private existsEventStream = new Subject<boolean>();
	private matterId: string;
	@Select(SelectedMatter.id) private selectedMatterId$: Observable<string>;
	@SelectSnapshot(SelectedMatterDocumentsState.getViewDocumentId)
	private viewingDocumentId!: string;
	@ViewSelectSnapshot(SelectedMatterDocumentsState.getViewingDocumentTitle)
	private viewingDocumentTitle!: string;
	@Select(DocumentComponentSelector.isDocumentLoading)
	private isDocumentLoading$!: Observable<boolean>;
	@Select(DocumentComponentSelector.breadcrumbItems)
	breadcrumbItems$!: Observable<BreadcrumbItem[]>;
	@Select(DocumentComponentSelector.isLoading) isLoading$: Observable<boolean>;
	@Select(SelectedMatterDocumentsState.documentActionsSelectionGroup)
	documentActions$!: Observable<SelectionGroup[]>;
	@Select(SelectedMatterDocumentsState.getViewingDocumentUpdating)
	documentUpdating$!: Observable<boolean>;
	@Select(SelectedMatterDocumentsState.canConvertTags)
	canConvertTags$!: Observable<boolean>;
	@Select(SelectedMatterDocumentsState.canResetDocument)
	canResetDocument$!: Observable<boolean>;
	documentBusy$ = new BehaviorSubject<boolean>(false);
	showTagsConvertedWarning$: Observable<boolean>;
	documentsOverview: string;
	ICON = ICON;

	constructor(
		private activatedRoute: ActivatedRoute,
		private navService: NavigationService,
		private selectedMatterDocumentDispatch: SelectedMatterDocumentsDispatch,
		private recentlyViewedDispatch: RecentlyViewedDispatch,
		private snackbar: SnackbarService,
		private dialog: MatDialog,
		private analytics: AnalyticsService,
	) {}

	ngOnInit(): void {
		// when the selected matter changes, and there is an id present
		// we need to store the matter and call initialization
		this.selectedMatterId$
			.pipe(
				untilDestroyed(this),
				distinctUntilChanged(),
				filter((id) => !!id),
			)
			.subscribe((id) => {
				this.matterId = id;
				this.init();
			});

		this.existsEventStream
			.pipe(
				untilDestroyed(this),
				debounceTime(DocumentComponent.nonExistantTimeoutMs),
			)
			.subscribe((loading) => {
				if (loading) {
					LogRocket.log(
						'Matter document not loading: redirecting to matter document list',
					);
					this.navService.goToMatter_Documents_SaleDocs(this.matterId);
					this.snackbar.openSnackBar(
						{
							text: 'Unable to load the document',
							type: 'error',
						},
						{
							duration: DocumentComponent.displayNonExistantMessageForMs,
						},
					);
				}
			});

		this.isDocumentLoading$
			.pipe(
				untilDestroyed(this),
				filter((loading) => !loading),
			)
			.subscribe((loading) => this.existsEventStream.next(loading));

		this.showTagsConvertedWarning$ = this.canConvertTags$.pipe(
			untilDestroyed(this),
			map((x) => !x),
		);
	}

	onActionSelect(evt: DropdownResult<DocumentResult>): void {
		const action = evt?.result?.value;
		const { viewingDocumentId } = this;
		if (action && viewingDocumentId) {
			if (action === DOCUMENT_ACTIONS.RENAME) {
				this.renameDocument(viewingDocumentId);
			} else {
				this.dispatchDocumentTagsAction(action);
			}
		}
	}

	dispatchDocumentTagsAction(
		action:
			| DOCUMENT_ACTIONS.CONVERT
			| DOCUMENT_ACTIONS.REVERT
			| DOCUMENT_ACTIONS.RESET,
	) {
		const { viewingDocumentId } = this;

		this.askForPermission(action).subscribe(() => {
			switch (action) {
				case DOCUMENT_ACTIONS.CONVERT:
					this.analytics.logEvent(AnalyticsEvent.DOCUMENT_CONVERT_TAGS_CLICK, {
						documentId: viewingDocumentId,
					});
					this.selectedMatterDocumentDispatch.ConvertTags(viewingDocumentId);
					tinymce.activeEditor.undoManager.clear();
					break;
				case DOCUMENT_ACTIONS.RESET:
					this.analytics.logEvent(
						AnalyticsEvent.DOCUMENT_RESET_DOCUMENT_CLICK,
						{ documentId: viewingDocumentId },
					);
					this.selectedMatterDocumentDispatch.ResetDocument(viewingDocumentId);
					tinymce.activeEditor.undoManager.clear();
					break;
				case DOCUMENT_ACTIONS.REVERT:
					this.analytics.logEvent(AnalyticsEvent.DOCUMENT_REVERT_TAGS_CLICK, {
						documentId: viewingDocumentId,
					});
					this.selectedMatterDocumentDispatch.RevertTags(viewingDocumentId);
					tinymce.activeEditor.undoManager.clear();
					break;
			}
		});
	}

	resetDocument() {
		this.dispatchDocumentTagsAction(DOCUMENT_ACTIONS.RESET);
	}

	onWorkingChange(working: boolean) {
		const { documentBusy$ } = this;
		if (working !== documentBusy$.getValue()) {
			documentBusy$.next(working);
		}
	}

	init(): void {
		const { matterId } = this;
		if (matterId) {
			this.documentsOverview =
				UrlService.Matter_Documents_SaleDocs_Overview(matterId);

			this.activatedRoute.params
				.pipe(untilDestroyed(this))
				.subscribe((params) => {
					this.recentlyViewedDispatch.UpdateViewedDocs(
						matterId,
						params.documentId,
					);
					this.selectedMatterDocumentDispatch.SelectedDocuments([
						params.documentId,
					]);
				});
		}
	}

	back(): void {
		this.navService.goToMatter_Documents_SaleDocs(this.matterId);
	}

	private askForPermission(convertType: DOCUMENT_ACTIONS): Observable<void> {
		return this.dialog
			.open(ProceedModalComponent, {
				data: documentActionData[convertType],
			})
			.afterClosed()
			.pipe(
				untilDestroyed(this),
				take(1),
				filter((response) => !!response),
			);
	}

	private renameDocument(documentId: string): void {
		if (documentId) {
			const data: InputModalData<string> = {
				heading: 'Edit document title',
				value: this.viewingDocumentTitle,
			};
			this.dialog
				.open(InputModalComponent, { data })
				.afterClosed()
				.pipe(
					untilDestroyed(this),
					take(1),
					filter((value) => value !== false),
				)
				.subscribe((title) => {
					this.selectedMatterDocumentDispatch.SetDocumentTitle(
						documentId,
						title,
					);
				});
		}
	}
}
